import type { CSSProperties } from 'react'
import { MARK_BOUNDS, MARK_PATH, MARK_STROKE, MARK_TRANSFORM } from '../lib/brandMark'

// The Matlist logo, drawn inline rather than as an <img> so its colour follows
// the theme: the mark is ink on light and lime on dark (--ml-mark), the name is
// live text in the self-hosted Archivo (.matlist-wordmark, brand.css). The
// geometry comes from brand/gen_icons.py through lib/brandMark.ts.

/** The bare mark. `cropped` trims its 100-unit box to the stroke, so it can
 *  stand on a text baseline at exactly cap height. `draw` animates it on. */
export function Mark({ height, color = 'var(--ml-mark)', cropped = false, draw = false }: {
  height: number
  color?: string
  cropped?: boolean
  draw?: boolean
}) {
  const [x, y, w, h] = cropped ? MARK_BOUNDS : [0, 0, 100, 100]
  return (
    <svg aria-hidden="true" viewBox={`${x} ${y} ${w} ${h}`} height={height} width={(height * w) / h}
      style={{ display: 'block', flexShrink: 0 }}>
      <g transform={MARK_TRANSFORM}>
        <path d={MARK_PATH} fill="none" stroke={color} strokeWidth={MARK_STROKE}
          strokeLinecap="round" strokeLinejoin="round"
          pathLength={draw ? 1 : undefined} className={draw ? 'ml-splash__draw' : undefined} />
      </g>
    </svg>
  )
}

// Archivo's cap height, as a fraction of the em: the mark is drawn this tall
// so its feet and the letters share a baseline and its top meets the M's.
const CAP_HEIGHT = 0.688

/** The logo lockup (ux/03): mark first, then "Matlist", `size` px to the em,
 *  a quarter of the size between them. */
export function Wordmark({ size, style }: { size: number; style?: CSSProperties }) {
  return (
    <span role="img" aria-label="Matlist" className="ml-lockup" style={{ gap: size * 0.25, ...style }}>
      <Mark height={size * CAP_HEIGHT} cropped />
      <span className="matlist-wordmark" aria-hidden="true" style={{ fontSize: size }}>Matlist</span>
    </span>
  )
}

/** The launch screen (ux/03 Splash): ink, the lime mark at 88 px drawing
 *  itself on, the name at 40 px in paper settling in 14 px below. Ink in both
 *  themes — it continues the manifest's background_color, so an installed app
 *  goes from the OS splash to this one without a flash. `leaving` fades it
 *  out over the list once the cold start is done. */
export function Splash({ leaving = false, overlay = false }: { leaving?: boolean; overlay?: boolean }) {
  return (
    <div
      role={overlay ? undefined : 'status'}
      aria-label={overlay ? undefined : 'Loading Matlist'}
      aria-hidden={overlay ? true : undefined}
      className={`ml-splash${overlay ? ' ml-splash--overlay' : ''}${leaving ? ' is-leaving' : ''}`}
    >
      {/* 88 px wide, as the brand kit's mark_lime_88 (88 × 52) */}
      <Mark height={52} color="var(--matlist-lime)" cropped draw />
      <span className="matlist-wordmark ml-splash__word">Matlist</span>
    </div>
  )
}
